import {z} from "zod";
import React from "react";
import {Trans, useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Alert, Box, Grid, Stack, Typography} from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {swallowException} from "../../utils/appUtils";
import {Order} from "../../types/domain/order";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {Farm} from "../../types/domain/farm";
import {useFarmManagement} from "../../hooks/farmsHooks";
import {LabeledInput} from "../base/LabeledInput";
import {Dialog} from "../base/Dialog";
import {TextInput} from "../base/TextInput";
import {useFieldManagement} from "../../hooks/fieldManagementHooks";
import {FarmEditFieldsTable} from "../farms/FarmEditFieldsTable";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean, companyResponse?: Order) => void;
    mode: "new" | "edit";
    companyId: string;
    selectedFarm?: Farm;
    setFarm?: React.Dispatch<React.SetStateAction<{id: string} | undefined>>;
}

export const FarmCreateDialog: React.FC<Props> = ({open, onClose, mode, companyId, selectedFarm}) => {
    const {t} = useTranslation();
    const INITIAL_FORM_VALUES: FormInputs = {
        name: t("dialogs.farm_create.initial_farm_name"),
        isDefault: false,
    };
    const farmManagement = useFarmManagement();
    const fieldManagement = useFieldManagement();
    const initFormValues = mode === "edit" && selectedFarm ? constructValues(selectedFarm) : INITIAL_FORM_VALUES;
    const {
        handleSubmit,
        register,
        formState: {errors},
    } = useForm({
        defaultValues: initFormValues,
        resolver: zodResolver(FARM_CREATE_SCHEMA),
    });

    const onSubmit = (data: FormInputs) => {
        farmManagement.reset();

        if (mode === "new") {
            farmManagement
                .create(companyId, {
                    name: data.name,
                    isDefault: false,
                })
                .then(() => {
                    onClose(true);
                })
                .catch(swallowException);
        } else {
            farmManagement
                .edit(companyId, selectedFarm!.id, {
                    name: data.name,
                    isDefault: data.isDefault,
                })
                .then(() => {
                    onClose(true);
                })
                .catch(swallowException);
        }
    };

    const onDelete = () => {
        farmManagement.reset();

        farmManagement
            .delete(companyId, selectedFarm!.id)
            .then(() => {
                onClose(true);
            })
            .catch(swallowException);
    };

    return (
        <Dialog
            enableSubmit
            enableDelete={mode === "edit" && selectedFarm?.isDefault !== true}
            title={mode === "edit" ? t("dialogs.farm_create.edit_title") : t("dialogs.farm_create.create_title")}
            titleIcon={<FolderOpenIcon color="primary" />}
            open={open}
            deleteConfirmation={t("dialogs.farm_create.delete_confirmation")}
            onClose={() => onClose(false)}
            onDelete={onDelete}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(onSubmit)}
        >
            <Grid container spacing={2}>
                {isResourceFailed(farmManagement.resource) && (
                    <Grid item xs={12}>
                        <Alert severity="error">{getResourceErrorMessage(farmManagement.resource)}</Alert>
                    </Grid>
                )}
                {isResourceFailed(fieldManagement.resource) && (
                    <Grid item xs={12}>
                        <Alert severity="error">{getResourceErrorMessage(fieldManagement.resource)}</Alert>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <LabeledInput label={t("dialogs.farm_create.name_title")}>
                                <TextInput
                                    name="name"
                                    type="text"
                                    register={register}
                                    errors={errors}
                                    disabled={initFormValues.isDefault}
                                />
                            </LabeledInput>
                        </Grid>

                        <Grid item xs={12}>
                            <Box bgcolor="white" border={1} borderColor="secondary.main">
                                {mode === "new" ? (
                                    <Box bgcolor="white" p={4}>
                                        <Stack
                                            spacing={2}
                                            direction="column"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <InfoIcon color="disabled" />
                                            <Typography color="#656B80">
                                                <Trans i18nKey="dialogs.farm_create.info_message"></Trans>
                                            </Typography>
                                        </Stack>
                                    </Box>
                                ) : (
                                    <FarmEditFieldsTable companyId={companyId} farmId={selectedFarm!.id} />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
};

interface FormInputs {
    name: string;
    isDefault: boolean;
}

const FARM_CREATE_SCHEMA = z.object({
    name: z.string(),
    isDefault: z.boolean(),
});

const constructValues = (farm: Farm): FormInputs => {
    return {
        ...farm,
    };
};
